import { isAccessToken, isRefreshToken, OktaAuth } from '@okta/okta-auth-js';

import { sendSentryError } from './utils/sendSentryError';

const issuer = process.env.OKTA_ISSUER || '';
const clientId = process.env.OKTA_CLIENT_ID || '';
const REFRESH_EARLY_SECONDS = 20 * 60; // 20 minutes in seconds

const oktaAuth: OktaAuth = new OktaAuth({
  issuer,
  clientId,
  redirectUri: `${window.location.origin}/login`,
  scopes: ['openid', 'profile', 'email', 'offline_access', 'idx_instancepermissions'],
  pkce: true,
  tokenManager: {
    storage: 'localStorage',
    storageKey: 'opal-app-okta-tokens',
    autoRenew: false, // Disable SDK auto-renewal to prevent iframe-based silent auth
  },
});

/**
 * Gets or renews the Okta access token if it's expiring within 20 minutes
 * @returns Promise<string> The access token (renewed if necessary)
 * @throws Error if unable to renew token or no token exists
 */
export const getOrRenewOptiIdToken = async (): Promise<string> => {
  try {
    const tokenManager = oktaAuth.tokenManager;
    const accessToken = await tokenManager.get('accessToken');

    if (!isAccessToken(accessToken)) {
      throw new Error('No access token found');
    }

    // Check if token expires within 20 minutes (1200 seconds)
    const currentTime = Math.floor(Date.now() / 1000);
    const expiresAt = accessToken.expiresAt || 0;
    const timeUntilExpiry = expiresAt - currentTime;

    if (timeUntilExpiry <= REFRESH_EARLY_SECONDS) {
      // Renew using refresh token only — never fall back to iframe-based silent auth,
      // which hangs when the IdP sets X-Frame-Options: DENY.
      const refreshToken = await tokenManager.get('refreshToken');
      if (!isRefreshToken(refreshToken)) {
        throw new Error('No refresh token available for renewal');
      }

      const renewedTokens = await oktaAuth.token.renewTokensWithRefresh(
        { scopes: oktaAuth.options.scopes },
        refreshToken
      );

      if (!isAccessToken(renewedTokens.accessToken)) {
        throw new Error('No access token found during renewal');
      }

      tokenManager.setTokens(renewedTokens);
      return renewedTokens.accessToken.accessToken;
    }
    // Token is still valid for more than threshold minutes
    return accessToken.accessToken;
  } catch (error) {
    const errorMessage = `Failed to renew access token: ${
      error instanceof Error ? error.message : 'Unknown error'
    }`;
    // Send error to Sentry before throwing
    sendSentryError(error instanceof Error ? error : new Error(errorMessage), {
      context: 'getOrRenewOptiIdToken',
      operation: 'token_renewal',
    });

    throw new Error(errorMessage);
  }
};

export default oktaAuth;
